import type { PendingCoverCropSuggestion } from "services";

import { axios } from "utils";

import { ADMIN_VERIFICATIONS_URL as endpoint } from "consts";

import {
  AddEditingRequestRequestBody,
  AddOrUpdateVerificationTask,
  Certification,
  Coordinates,
  GetFarmDocScanFieldResponse,
  GetFarmDocScanFieldsResponse,
  GetFarmDocScanSummaryResponse,
  GetFarmVerificationsResponse,
  GetFieldPracticePointsResponse,
  GetFieldVerificationsQuery,
  GetFieldVerificationsResponseBody,
  GetVerificationEditingRequestsResponse,
  GetVerificationStatusUploadsResponse,
  PayoutApprovalAdminResponse,
  ProgramVerificationPoints,
  RecalculationTrigger,
  ReviewedDataPoint,
  UpdateEditingRequestRequestBodyAdmin,
  VerificationStatusUploadRow,
} from "models";

type VerificationState = "verified" | "declined" | "pending";

type FieldVerificationUpdate = {
  fieldVerificationId: string | null;
  fieldVerificationState: VerificationState | null;
};

// farms

export const getFarmVerifications = async (data: {
  filter?: string;
  assigneeId?: string;
  carbonProgramId?: string;
  search: string;
  page: number;
  itemsPerPage: number;
  includeAll?: boolean;
  onlySoilPlus3?: boolean;
}): Promise<GetFarmVerificationsResponse> => axios.get(`${endpoint}/farms`, { data });

export const getFarmVerificationSummary = async (data: { farmId: string }) => axios.get(`${endpoint}/farms/${data.farmId}/summary`, { data });

export const getFieldPracticePoints = async (data: { farmId: string }): Promise<GetFieldPracticePointsResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/verification-points`, { data });

export const updateFarmReviewer = async (data: { farmId: string; reviewerId: string; signal: AbortSignal }) =>
  axios.put(`${endpoint}/farms/${data.farmId}/reviewer`, { data: { reviewerId: data.reviewerId }, signal: data.signal });

export const verifyFarm = async (data: { id: string; signal: AbortSignal }) => axios.put(`${endpoint}/farms/${data.id}`, { data, signal: data.signal });

export const updateFarmVerificationSize = async (data: { size: number; farmId: string }): Promise<void> =>
  axios.put(`${endpoint}/farms/${data.farmId}/size`, { data });

// field

export const verifyField = async (data: { verified: boolean | null; verificationId: string }): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/fields/${data.verificationId}`, { data });

export const updateFieldVerificationSize = async (data: { size: number; verificationId: string }): Promise<void> =>
  axios.put(`${endpoint}/fields/${data.verificationId}/size`, { data });

export const updateFieldVerificationOfficialSize = async (data: {
  officialSize: number | null;
  verificationId: string;
}): Promise<{ officialSize: number | null }> => axios.put(`${endpoint}/fields/${data.verificationId}/official-size`, { data });

// Batch update (used for csv upload for example)

export const verifyFields = async (data: FieldVerificationUpdate[]) => axios.put(`${endpoint}/fields`, { data });

export const getFieldVerifications = async (data: GetFieldVerificationsQuery): Promise<GetFieldVerificationsResponseBody> =>
  axios.get(`${endpoint}/farms/${data.farmId}/fields`, { data });

export type AdminCoverCropDetection = PendingCoverCropSuggestion & {
  coverCropPredicted: boolean;
  linkedFieldCropId: string | null;
  modelName: string | null;
  modelVersion: string | null;
  source: string;
  /** The verifier's feedback: true when the detection is correct, false when it is wrong. */
  verifierIsCorrect: boolean | null;
};

export const getFarmCoverCropDetections = async (data: { farmId: string }): Promise<AdminCoverCropDetection[]> =>
  axios.get(`${endpoint}/farms/${data.farmId}/cover-crop-detections`, { data });

export type VerifierDetectionFeedback = { detectionId: string; isCorrect: boolean };

export const decideFarmDetection = async (data: VerifierDetectionFeedback & { farmId: string }): Promise<VerifierDetectionFeedback> =>
  axios.put(`${endpoint}/farms/${data.farmId}/detections/${data.detectionId}/decision`, { data: { isCorrect: data.isCorrect } });

export type MainCropSuggestion = {
  /** The model's English name. */
  cropName: string;
  /** Null when the platform has no such crop. */
  cropSlug: string | null;
  sharePct: number;
};

export type AdminMainCropDetection = {
  detectionId: string;
  fieldId: string;
  fieldName: string;
  seasonYear: number;
  predictedClass: string | null;
  /** Of the predicted class, not of the crop. */
  confidence: number | null;
  confidenceTier: string | null;
  lowConfidence: boolean;
  /** Top 3, best first. Empty for review results. */
  rankedSuggestions: MainCropSuggestion[];
  modelName: string | null;
  modelVersion: string | null;
  source: string;
  verifierIsCorrect: boolean | null;
  detectedAt: string | null;
};

// Satellite data of one field (remote sensing service, through the API). Crop agnostic: a field and dates in.

export type SatelliteImageLayer = "true-color" | "ndvi";

export type SatellitePass = {
  date: string;
  ndvi: number | null;
  fieldCloudCoverPct: number | null;
  radarVhDb: number | null;
  radarVvDb: number | null;
  hasImage: boolean;
};

export type SatelliteDateRange = { from: string; to: string };

export type FieldSatelliteObservations = {
  range: SatelliteDateRange;
  passes: SatellitePass[];
};

export type FieldSatelliteThumbnails = {
  layer: SatelliteImageLayer;
  thumbnails: { date: string; url: string }[];
  noImage: string[];
  failed: string[];
};

export type FieldSatelliteImage = { date: string; layer: SatelliteImageLayer; url: string; width: number; height: number };

const satelliteUrl = (farmId: string, fieldId: string) => `${endpoint}/farms/${farmId}/fields/${fieldId}/satellite`;

// The remote sensing tab shows its own error states (and a background prefetch may fail unseen), so no error toast.
const satelliteRequest = { hideErrorMessage: true };

export const getFarmMainCropDetections = async (data: { farmId: string }): Promise<AdminMainCropDetection[]> =>
  axios.get(`${endpoint}/farms/${data.farmId}/main-crop-detections`, { ...satelliteRequest, data });

export const getFieldSatelliteObservations = async (data: {
  farmId: string;
  fieldId: string;
  range: SatelliteDateRange;
}): Promise<FieldSatelliteObservations> => axios.get(`${satelliteUrl(data.farmId, data.fieldId)}/observations`, { ...satelliteRequest, params: data.range });

export const getFieldSatelliteThumbnails = async (data: {
  farmId: string;
  fieldId: string;
  range: SatelliteDateRange;
  layer: SatelliteImageLayer;
}): Promise<FieldSatelliteThumbnails> =>
  axios.get(`${satelliteUrl(data.farmId, data.fieldId)}/thumbnails`, { ...satelliteRequest, params: { ...data.range, layer: data.layer } });

export const getFieldSatelliteImage = async (data: {
  farmId: string;
  fieldId: string;
  date: string;
  layer: SatelliteImageLayer;
}): Promise<FieldSatelliteImage> =>
  axios.get(`${satelliteUrl(data.farmId, data.fieldId)}/images/${data.date}`, { ...satelliteRequest, params: { layer: data.layer } });

export const getFieldVerificationSummary = async (data: { farmId: string }) => axios.get(`${endpoint}/farms/${data.farmId}/fields/summary`, { data });

export const getFieldVerificationShape = async (data: {
  verificationId: string;
  signal?: AbortSignal;
}): Promise<{ fieldShape: { geometry: Coordinates[]; center: Coordinates } }> =>
  axios.get(`${endpoint}/fields/${data.verificationId}/shape`, { data, signal: data.signal });

// comments

export const addVerificationComment = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/comments`, { data, signal: data.signal });

export const getVerificationComments = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/comments`, { data, signal: data.signal });

// tasks

export const getAllVerificationTasks = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/tasks`, { data, signal: data.signal });

export const addOrUpdateVerificationTask = async (data: AddOrUpdateVerificationTask) => axios.post(`${endpoint}/tasks`, { data });

export const removeVerificationTask = async (data: { id: string; signal: AbortSignal }) =>
  axios.delete(`${endpoint}/tasks/${data.id}`, { data, signal: data.signal });

export const addVerificationFarmTask = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/farms/${data.farmId}/tasks`, { data, signal: data.signal });

export const updateUserVerificationFarmTaskAdmin = async (data: { id: string; signal: AbortSignal }) =>
  axios.put(`${endpoint}/farms/tasks/${data.id}`, { data, signal: data.signal });

export const getAllFarmVerificationTasks = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/farms/${data.farmId}/tasks`, { data, signal: data.signal });

// payouts

export const getAdminFarmPayoutApprovals = async (data: { farmId: string }): Promise<PayoutApprovalAdminResponse[]> =>
  axios.get(`${endpoint}/farms/${data.farmId}/payout-approvals`, { data });

export const approvePayout = async (data: {
  farmId: string;
  signal: AbortSignal;
  trustStatus?: "very" | "normal" | "questionable";
  adminNotes?: string;
  verifier?: string;
}) => axios.post(`${endpoint}/farms/${data.farmId}/payout-approvals/approve`, { data, signal: data.signal });

export const rejectPayout = async (data: { farmId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/farms/${data.farmId}/payout-approvals/reject`, { data, signal: data.signal });

export const revertPayoutApproval = async (data: { farmId: string }) => axios.post(`${endpoint}/farms/${data.farmId}/payout-approvals/revert`);

// editing requests

export const addEditingRequest = async (data: AddEditingRequestRequestBody) => axios.post(`${endpoint}/editing-requests`, { data });

export const updateEditingRequestAdmin = async (data: UpdateEditingRequestRequestBodyAdmin) => axios.put(`${endpoint}/editing-requests/${data.id}`, { data });

export const getVerificationEditingRequestsAdmin = async (data: { farmId: string }): Promise<GetVerificationEditingRequestsResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/editing-requests`, { data });

// doc scans

export const getFarmDocScanSummary = async (data: { farmId: string }): Promise<GetFarmDocScanSummaryResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/doc-scans/summary`, { data });

export const getFarmDocScanFields = async (data: { farmId: string }): Promise<GetFarmDocScanFieldsResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/doc-scans/fields`, { data });

export const getFarmDocScanField = async (data: { farmId: string; scanFieldId: string }): Promise<GetFarmDocScanFieldResponse> =>
  axios.get(`${endpoint}/farms/${data.farmId}/doc-scans/fields/${data.scanFieldId}`, { data });

// crop verification status

export const updateVerificationStatus = async (data: {
  practiceType: string;
  practiceId: string;
  verificationStatus: string | null;
}): Promise<{ success: boolean }> => axios.put(`${endpoint}/${data.practiceType}/${data.practiceId}/verification-status`, { data });

// crop and organic fertilizer reviewed data points

export const updateReviewedDataPoints = async (data: {
  practiceType: string;
  practiceId: string;
  reviewedDataPoints: ReviewedDataPoint[] | null;
}): Promise<{ success: boolean }> => axios.put(`${endpoint}/${data.practiceType}/${data.practiceId}/reviewed-data-points`, { data });

// Recalculation triggers

export const addRecalculationTrigger = async (data: Pick<RecalculationTrigger, "title" | "description">): Promise<RecalculationTrigger> =>
  axios.post(`${endpoint}/recalculation-triggers`, { data });

export const getRecalculationTrigger = async (data: { id: string }): Promise<RecalculationTrigger> =>
  axios.get(`${endpoint}/recalculation-triggers/${data.id}`);

export const getLatestRecalculationTrigger = async (): Promise<RecalculationTrigger | null> => axios.get(`${endpoint}/recalculation-triggers/latest`);

// Verification status uploads

export const uploadVerificationStatus = async (data: { rows: VerificationStatusUploadRow[] }) =>
  axios.post(`${endpoint}/verification-status-uploads`, { data });

export const getVerificationStatusUploads = async (data: { page: number; limit: number }): Promise<GetVerificationStatusUploadsResponse> =>
  axios.get(`${endpoint}/verification-status-uploads`, { data });

// certifications

export const getCertifications = async (data: { year: number }): Promise<Certification[]> => axios.get(`${endpoint}/certifications`, { data });

export const certifyProgram = async (data: { programId: string; year: number }) => axios.post(`${endpoint}/certifications`, { data });

export const getProgramsVerificationPoints = async (data: { programIds: string[]; year: number }): Promise<ProgramVerificationPoints[]> =>
  axios.post(`${endpoint}/programs/verification-points`, { data });
